import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import useDebounce from '../../helper/useDebounce';
import { selectFormValidity, setCardholderName } from './checkoutSlice';
import { fontSize, wh } from '../../mixins';
import UIButton from '../../UI/UIButton';

const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const StyledWrapper = styled.div`
  position: relative;
  z-index: 10;
  margin: 0;

  & > * {
    position: relative;
    z-index: 10;
  }
`;

const StyledCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: ${isArabic ? 'flex-end' : 'flex-start'};
  position: relative;
  background-color: #fff;
  z-index: 10;
    
  & > * {
    position: relative;
    z-index: 10;
    
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

const StyledFieldWrapper = styled.div`
  width: 100%;
  height: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #F9F9FA;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  padding: 16px 12px;
  
  & > div {
    height: 100%;
  }
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  
  & > div {
    flex: 1 0 40%;
    
    &:not(:last-child) {
      margin-right: 20px;
    }
  }
`;

const StyledInput = styled.input`
  ${wh('100%', '22px')};
  ${fontSize(16)};
  line-height: 22px;
  color: #323232;
  padding: 0;
  background-color: transparent;
  border: none;
  text-align: ${textAlign};
  
  &::-webkit-credit-card-auto-fill-button {
    visibility: hidden;
    display: none !important;
    pointer-events: none;
    position: absolute;
    right: 0;
  }
  
  &::placeholder {
    font-weight: 400;
    ${fontSize(16)};
    line-height: 22px;
    color: #909090;
  }
  
  &:focus,
  &:hover,
  &:active {
    outline: none;
  }
`;

const StyledBrandIcon = styled.span`
  position: absolute;
  right: 16px;
  left: 16px;
  ${textAlign}: auto;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardForm = ({ method, card, expiration, cvc, cardholder, cardBrand, renderBrand, onPurchaseClick }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const dispatch = useDispatch();
    const debouncedName = useDebounce(name, 500);
    const isStripe = method === 'stripe';
    const isFormValid = useSelector(selectFormValidity);
    useEffect(() => {
        if (debouncedName) {
            dispatch(setCardholderName(debouncedName));
        }
    }, [debouncedName, dispatch]);

    return (
      <>
        <StyledWrapper>
            <StyledCard>
                <StyledFieldWrapper>
                    <div id={'card-number-element'} ref={!isStripe ? card : null}/>
                    <StyledBrandIcon>{renderBrand(cardBrand)}</StyledBrandIcon>
                </StyledFieldWrapper>
                <StyledRow>
                    <StyledFieldWrapper>
                        <div id={'card-expiry-element'} ref={!isStripe ? expiration : null}/>
                    </StyledFieldWrapper>
                    <StyledFieldWrapper>
                        <div id={'card-cvc-element'} ref={!isStripe ? cvc : null}/>
                    </StyledFieldWrapper>
                </StyledRow>
                <StyledFieldWrapper>
                  <StyledInput id={'cardholder-name-element'} ref={cardholder} onChange={e => setName(e.target.value)} type='text' autocomplete='cc-name' placeholder={t('name_on_card')}/>
                </StyledFieldWrapper>
            </StyledCard>
        </StyledWrapper>
        <UIButton onClick={onPurchaseClick} disabled={!isFormValid} customId={`${method}-continue-button`}/>
        </>
    );
};


export default CardForm;
